import React, { useState, useEffect } from "react";
import { Subscription } from "../types/blog";
import { getAllSubscribers, removeSubscriber } from "../utils/subscriptionUtils";
import LoadingSpinner, { LoadingTable } from "./LoadingSpinner";

interface SubscriberManagerProps {
  onClose?: () => void;
}

const SubscriberManager: React.FC<SubscriberManagerProps> = ({ onClose }) => {
  const [subscribers, setSubscribers] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingEmails, setDeletingEmails] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadSubscribers();
  }, []);

  const loadSubscribers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllSubscribers();
      setSubscribers(data);
    } catch (err) {
      console.error("Error loading subscribers:", err);
      setError("Failed to load subscribers");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveSubscriber = async (email: string) => {
    if (!window.confirm(`Remove subscriber: ${email}?`)) return;

    try {
      setDeletingEmails((prev) => new Set(prev).add(email));
      await removeSubscriber(email);
      setSubscribers((prev) => prev.filter((sub) => sub.email !== email));
    } catch (err) {
      console.error("Error removing subscriber:", err);
      setError("Failed to remove subscriber");
    } finally {
      setDeletingEmails((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
    }
  };

  const filteredSubscribers = subscribers.filter((subscriber) =>
    subscriber.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const newSubscribersCount = subscribers.filter((sub) => sub.isNew).length;

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-12">
          <div className="mb-4">
            <svg
              className="w-16 h-16 text-red-500 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{error}</h3>
          <button
            onClick={loadSubscribers}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Subscriber Management
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {subscribers.length} total subscribers
            {newSubscribersCount > 0 && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                {newSubscribersCount} new (last 7 days)
              </span>
            )}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search subscribers by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <svg
            className="w-5 h-5 text-gray-400 absolute right-3 top-2.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {isLoading ? (
        <LoadingTable rows={5} columns={3} />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="py-3 px-4 text-gray-500 font-medium">Email</th>
                <th className="py-3 px-4 text-gray-500 font-medium">
                  Subscribed
                </th>
                <th className="py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSubscribers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-8 text-center text-gray-500">
                    {searchQuery
                      ? `No subscribers found matching "${searchQuery}"`
                      : "No subscribers yet"}
                  </td>
                </tr>
              ) : (
                filteredSubscribers.map((subscriber) => (
                  <tr
                    key={subscriber.email}
                    className={`hover:bg-gray-50 ${
                      subscriber.isNew ? "bg-green-50" : ""
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <span className="text-gray-800">{subscriber.email}</span>
                        {subscriber.isNew && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            NEW
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      <div>
                        {new Date(subscriber.date).toLocaleDateString()}
                        <div className="text-xs text-gray-500">
                          {new Date(subscriber.date).toLocaleTimeString()}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleRemoveSubscriber(subscriber.email)}
                        disabled={deletingEmails.has(subscriber.email)}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        title="Remove subscriber"
                      >
                        {deletingEmails.has(subscriber.email) ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing {filteredSubscribers.length} of {subscribers.length}{" "}
            subscribers
            {searchQuery && ` matching "${searchQuery}"`}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriberManager;